/**
 * Users service with TanStack Query hooks
 * Provides hooks for fetching all users and user profiles
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../lib/api-client';

/**
 * Fetch all users (family members list)
 */
const fetchAllUsers = async (): Promise<{ items: User[]; total: number }> => {
  const response = await apiClient.get('/users');
  return response.data;
};

/**
 * Fetch user by userId
 */
const fetchUserById = async (userId: string): Promise<User> => {
  try {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      throw new Error('User not found');
    }
    throw new Error(`Failed to fetch user: ${error.message}`);
  }
};

/**
 * Hook to fetch all users
 */
export const useAllUsers = () => {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['users', 'all'],
    queryFn: fetchAllUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!idToken,
  });
};

/**
 * Hook to fetch user by ID
 */
export const useUserById = (userId: string | undefined) => {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['users', userId],
    queryFn: () => fetchUserById(userId!),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!idToken && !!userId,
  });
};

/**
 * Hook to fetch user's photos
 */
export const useUserPhotos = (userId: string | undefined) => {
  const { idToken } = useAuth();

  return useQuery({
    queryKey: ['users', userId, 'photos'],
    queryFn: async () => {
      const response = await apiClient.get(`/users/${userId}/photos`);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled: !!idToken && !!userId,
  });
};

/**
 * Delete a user (admin only)
 */
const deleteUser = async (userId: string): Promise<void> => {
  const response = await apiClient.delete(`/admin/users/${userId}`);
  return response.data;
};

/**
 * Hook to delete a user (admin only)
 */
export const useDeleteUser = () => {
  const { idToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => {
      if (!idToken) throw new Error('Not authenticated');
      return deleteUser(userId);
    },
    onSuccess: () => {
      // Invalidate users list to refresh after deletion
      queryClient.invalidateQueries({ queryKey: ['users', 'all'] });
    },
  });
};
